/**
 * TikTok Connect Panel — Neuralia369
 * Conecta TikTok via OAuth directo. Muestra estado y permite publicar.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, XCircle, RefreshCw, ExternalLink, Play, Video, Music2, TrendingUp, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TikTokConnectPanel({ authFetch }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [toast, setToast] = useState('');

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 4000); };

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/social/tiktok/status`);
      const d = await res.json();
      setStatus(d);
    } catch (e) {
      showToast('Error cargando estado TikTok');
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Escuchar el callback OAuth de TikTok
  useEffect(() => {
    const handler = (e) => {
      if (e.data?.type === 'tiktok_oauth_success') {
        showToast('TikTok conectado correctamente');
        loadStatus();
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [loadStatus]);

  const startOAuth = async () => {
    setConnecting(true);
    try {
      const res = await authFetch(`${API}/social/tiktok/oauth/start`);
      const d = await res.json();
      if (!d.auth_url) throw new Error('No se pudo iniciar OAuth');
      const popup = window.open(d.auth_url, 'TikTok OAuth',
        'width=600,height=700,left=200,top=100');
      // Revisar si el popup cerró
      const check = setInterval(() => {
        if (popup?.closed) {
          clearInterval(check);
          setConnecting(false);
          loadStatus();
        }
      }, 1000);
    } catch (e) {
      showToast(`Error: ${e.message}`);
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('¿Desconectar TikTok?')) return;
    try {
      await authFetch(`${API}/social/tiktok/disconnect`, { method: 'DELETE' });
      showToast('TikTok desconectado');
      loadStatus();
    } catch (e) {
      showToast('Error al desconectar');
    }
  };

  const isConnected = status?.account != null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-[#010101] border border-[#fe2c55]/30 flex items-center justify-center">
            <Music2 className="w-6 h-6 text-[#fe2c55]" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">TikTok</h2>
            <p className="text-slate-400 text-sm">Conecta y publica directamente desde Neuralia369</p>
          </div>
        </div>
        <button onClick={loadStatus} disabled={loading}
          className="p-2 bg-slate-800 border border-slate-700 text-slate-400 rounded-xl hover:bg-slate-700 transition-colors">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Status card */}
      <div className={`rounded-2xl border p-5 flex items-center justify-between gap-4 ${
        isConnected ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-slate-900/50 border-slate-800'
      }`}>
        <div className="flex items-center gap-3">
          {isConnected
            ? <CheckCircle2 className="w-6 h-6 text-emerald-400 flex-shrink-0" />
            : <XCircle className="w-6 h-6 text-slate-500 flex-shrink-0" />
          }
          <div>
            <p className={`font-semibold ${isConnected ? 'text-emerald-400' : 'text-slate-300'}`}>
              {isConnected ? `Conectado como @${status.account?.display_name || status.account?.open_id?.slice(0, 8) || 'tiktok'}` : 'Sin conectar'}
            </p>
            <p className="text-slate-500 text-sm">
              {isConnected ? 'TikTok Business conectado — listo para publicar' : 'Conecta tu cuenta para publicar vídeos automáticamente'}
            </p>
          </div>
        </div>
        {isConnected ? (
          <button onClick={disconnect}
            className="px-4 py-2 bg-red-500/10 border border-red-500/20 text-red-400 rounded-xl text-sm hover:bg-red-500/20 transition-colors">
            Desconectar
          </button>
        ) : (
          <button
            data-testid="tiktok-connect-btn"
            onClick={startOAuth}
            disabled={connecting}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#fe2c55] text-white font-bold rounded-xl hover:bg-[#e01e45] transition-colors disabled:opacity-50"
          >
            {connecting ? (
              <><svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg> Conectando...</>
            ) : (
              <><Music2 className="w-4 h-4" /> Conectar TikTok</>
            )}
          </button>
        )}
      </div>

      {/* Pasos de configuración */}
      {!isConnected && status?.setup_steps && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5">
          <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
            <Play className="w-4 h-4 text-[#fe2c55]" /> Requisitos previos
          </h3>
          <ol className="space-y-2">
            {status.setup_steps.map((step, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-slate-400">
                <span className="w-5 h-5 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-xs text-slate-500 flex-shrink-0 mt-0.5">
                  {i + 1}
                </span>
                {step}
              </li>
            ))}
          </ol>
          <div className="mt-4 pt-4 border-t border-slate-800">
            <p className="text-xs text-slate-600">
              Redirect URI configurado:
              <code className="ml-1 text-[#00d4ff] bg-slate-800 px-1.5 py-0.5 rounded text-xs">
                {process.env.REACT_APP_BACKEND_URL?.replace('/api','')}/tiktok/oauth/callback
              </code>
            </p>
            <a href="https://developers.tiktok.com" target="_blank" rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-1 text-xs text-[#fe2c55] hover:underline">
              <ExternalLink className="w-3 h-3" /> Abrir TikTok Developers
            </a>
          </div>
        </div>
      )}

      {/* Stats si conectado */}
      {isConnected && (
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Vídeos publicados', value: status?.posts?.length || 0, icon: Video },
            { label: 'Seguidores', value: status?.account?.follower_count || '—', icon: Users },
            { label: 'Likes totales', value: status?.account?.likes_count || '—', icon: TrendingUp },
          ].map(({ label, value, icon: Icon }) => (
            <div key={label} className="bg-slate-900/50 border border-slate-800 rounded-2xl p-4 text-center">
              <Icon className="w-5 h-5 text-[#fe2c55] mx-auto mb-1" />
              <div className="text-2xl font-bold text-white">{value}</div>
              <div className="text-slate-500 text-xs mt-0.5">{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Link al hub completo */}
      <a href="/admin/redes" target="_blank" rel="noopener noreferrer"
        className="flex items-center justify-center gap-2 w-full py-3 bg-slate-800 border border-slate-700 text-slate-300 rounded-xl hover:bg-slate-700 transition-colors text-sm">
        <ExternalLink className="w-4 h-4" />
        Abrir Hub Completo de Redes Sociales
      </a>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-slate-800 border border-slate-700 text-white text-sm px-5 py-3 rounded-xl shadow-xl z-50">
          {toast}
        </div>
      )}
    </div>
  );
}
